import { EmptyState, StepIndicatorStatus } from '@today/ui-components'
import { WizardShell, type WizardStep } from '@today/ui-recipes'
import type { ReactElement, ReactNode } from 'react'
import type { Translate } from './offer-types'

/** The wizard's step keys, in order; the catalog key `wizard.steps.<key>` labels each. */
export const OFFER_WIZARD_STEP_KEYS = ['recipient', 'products', 'preview', 'send'] as const

/**
 * Props for {@link OfferWizardShellView}.
 *
 * @property activeStep - Index into {@link OFFER_WIZARD_STEP_KEYS} of the current step.
 * @property children - The active step's content; when absent the empty state renders.
 * @property error - The create call's error message, shown above the step.
 * @property onBack - Goes to the previous step; no Back button when absent (TO-125).
 * @property onNext - Goes to the next step; no Next button when absent (TO-125).
 * @property nextDisabled - Disables Next (e.g. no recipient chosen yet, or a request in flight).
 */
export interface OfferWizardShellViewProps {
	t: Translate
	activeStep: number
	error: string | null
	children?: ReactNode
	onBack?: () => void
	onNext?: () => void
	nextDisabled?: boolean
}

/** A completed step is before the active one; the active one is in progress; the rest are not started. */
function stepStatusFor(index: number, activeStep: number): StepIndicatorStatus {
	if (index < activeStep) return StepIndicatorStatus.COMPLETE
	if (index === activeStep) return StepIndicatorStatus.IN_PROGRESS
	return StepIndicatorStatus.NOT_STARTED
}

/**
 * The offer wizard container: the title plus the shared {@link WizardShell} —
 * the four-step indicator, the error banner and the step outlet (TO-652 unifies
 * this onto WizardShell). The offer wizard is linear: steps are shown but never
 * selectable, so the shell wires no jump-to-step.
 */
export function OfferWizardShellView({
	t,
	activeStep,
	error,
	children,
	onBack,
	onNext,
	nextDisabled,
}: OfferWizardShellViewProps): ReactElement {
	const steps: WizardStep[] = OFFER_WIZARD_STEP_KEYS.map((key, index) => ({
		id: key,
		label: t(`wizard.steps.${key}`),
		status: stepStatusFor(index, activeStep),
		selectable: false,
	}))
	return (
		<section className="flex flex-col gap-4">
			<h1 className="text-ds-xl font-semibold text-ds-text">{t('wizard.title')}</h1>
			<WizardShell
				steps={steps}
				currentId={OFFER_WIZARD_STEP_KEYS[activeStep]}
				onBack={onBack}
				onNext={onNext}
				nextDisabled={nextDisabled}
				error={error}
				emptyFallback={<EmptyState title={t('wizard.empty')} />}
				labels={{ region: t('wizard.stepsLabel'), back: t('wizard.back'), next: t('wizard.next') }}
			>
				{children}
			</WizardShell>
		</section>
	)
}
